<script setup lang="ts">
import { type PropType } from 'vue';
import { gaiaFonts, gaiaFontSize, gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** The clause wording, verbatim. Line breaks are the drafter's and are kept. */
  text: {
    type: String,
    default: '',
  },
  /**
   * panel = the bordered, tinted box of the clause drawer and the clause form's
   * preview; plain = the text alone, for a caller that already owns the chrome
   * (the playbook wizard renders it inside a `GaiaSubPanel` labelled
   * "Clause text").
   */
  tone: {
    type: String as PropType<'panel' | 'plain'>,
    default: 'panel',
  },
  /** Shown in place of the text when a clause has none — never left blank. */
  placeholder: {
    type: String,
    default: 'This clause has no content.',
  },
});

const border = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surfaceSoft;
const ink = gaiaNeutral.ink;
const faint = gaiaNeutral.textFaint;
const serif = gaiaFonts.prose;
const size = gaiaFontSize.prose;
</script>

<template>
  <!-- Clause wording, set as it will read in the contract: the serif, the
       drafter's own line breaks, and prose leading. Not a GaiaText because the
       point is the voice change — GaiaText's job is UI copy, and its sizes are
       the UI scale, not a reading measure. -->
  <div
    class="gaia-clause-text"
    :class="[`gaia-clause-text--${props.tone}`, { empty: !props.text }]"
  >
    {{ props.text || props.placeholder }}
  </div>
</template>

<style scoped>
/* Stays CSS: `white-space: pre-wrap` and a prose line-height have no naive
   var, and the serif is a token rather than a literal. */
.gaia-clause-text {
  min-width: 0;
  font-family: v-bind(serif);
  font-size: v-bind(size);
  line-height: 1.7;
  color: v-bind(ink);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-wrap: pretty;
}

.gaia-clause-text--panel {
  padding: 16px 18px;
  border: 1px solid v-bind(border);
  border-radius: 11px;
  background: v-bind(surface);
}

/* An absent clause body is a fact about the record, not wording — so it drops
   out of the serif voice and reads as UI copy. */
.gaia-clause-text.empty {
  font-family: inherit;
  font-style: italic;
  color: v-bind(faint);
}
</style>
